// Groups of systems (Settings → Systems: a system's group, e.g. "Monitoring"): a view hides the groups its URL names
// (`hide=Monitoring`, repeated; a history step each way), so the integrations of a group can be looked past without
// leaving them out of the data. Pure helpers; the views keep the choice in the URL.
import type { Activity, Integration } from './api.ts';
import type { SystemInfo } from './systems.ts';

export const HIDE_KEY = 'hide';

/** The groups in use, each with its systems' codes, by name. */
export function groupsOf(systems: Iterable<SystemInfo>): { name: string; codes: string[] }[] {
  const by = new Map<string, string[]>();
  for (const s of systems) if (s.group) by.set(s.group, [...(by.get(s.group) ?? []), s.code]);
  return [...by].map(([name, codes]) => ({ name, codes: codes.sort() })).sort((a, b) => a.name.localeCompare(b.name));
}

export const readHidden = (p: URLSearchParams): string[] => [...new Set(p.getAll(HIDE_KEY))];

export function withHidden(p: URLSearchParams, groups: readonly string[]): URLSearchParams {
  const next = new URLSearchParams(p);
  next.delete(HIDE_KEY);
  for (const g of [...groups].sort()) next.append(HIDE_KEY, g);
  return next;
}

/** The system codes (and their aliases, upper case) the hidden groups hold. */
export function hiddenSystems(systems: Iterable<SystemInfo>, hidden: readonly string[]): Set<string> {
  const out = new Set<string>();
  if (!hidden.length) return out;
  for (const s of systems) if (s.group && hidden.includes(s.group)) for (const c of [s.code, ...s.aliases]) out.add(c.toUpperCase());
  return out;
}

const up = (s: string | null | undefined) => (s ?? '').toUpperCase();

/**
 * Activity without the hidden systems: an integration goes when its own system or a system its data is in is hidden;
 * a data model goes when its system is hidden or only hidden integrations touch it; the newest documents, the
 * failing and the silent go with them. Counts of what is left are as they were.
 */
export function filterActivity(a: Activity, hidden: ReadonlySet<string>): Activity {
  if (hidden.size === 0) return a;
  const systemOf = new Map(a.byClass.map((c) => [c.cls, up(c.system)]));
  const hiddenFlow = (f: Activity['flows'][number]) => hidden.has(up(f.system)) || [f.from, ...f.to].some((c) => hidden.has(systemOf.get(c) ?? ''));
  const flows = a.flows.filter((f) => !hiddenFlow(f));
  const goneProcesses = new Set(a.flows.filter(hiddenFlow).map((f) => f.process));
  const inVisible = new Set(flows.flatMap((f) => [f.from, ...f.to]));
  const inAny = new Set(a.flows.flatMap((f) => [f.from, ...f.to]));
  const byClass = a.byClass.filter((c) => !hidden.has(up(c.system)) && (inVisible.has(c.cls) || !inAny.has(c.cls)));
  const kept = new Set(byClass.map((c) => c.cls));
  const keepProcess = (p: string | null) => !p || !goneProcesses.has(p);
  return {
    ...a, flows, byClass,
    latest: a.latest.filter((d) => kept.has(d.cls) && keepProcess(d.process)),
    silent: a.silent.filter((s) => keepProcess(s.process)),
    failing: a.failing?.filter((f) => keepProcess(f.process)),
  };
}

/** Integrations without those that read or write a hidden system. */
export function filterIntegrations<T extends Pick<Integration, 'from' | 'to'>>(list: readonly T[], hidden: ReadonlySet<string>): T[] {
  if (hidden.size === 0) return [...list];
  return list.filter((i) => !hidden.has(up(i.from)) && !hidden.has(up(i.to)));
}

/** A search's own words with the hidden systems left out (`-@system=DATADOG …`). */
export function withoutSystems(q: string, hidden: ReadonlySet<string>): string {
  return [q, ...[...hidden].sort().map((c) => `-@system=${c}`)].filter(Boolean).join(' ');
}
